import Banner from '@/components/Banner';
import ImageGallery from '@/components/ImageGallery';
import { Reveal } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import { Spine, SpineItem } from '@/components/Spine';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';

const BASE = '/storage/images/about/special-events/event-management';

interface Event {
  title: string;
  description: string;
  /** Directory under BASE holding this event's photographs. */
  path: string;
  count: number;
}

const events: Event[] = [
  {
    title: 'Miss Trichy and Mrs Trichy (2022)',
    description:
      "A glamorous spectacle infusing sophistication and elegance into the Academy's repertoire, capturing the essence of beauty and grace.",
    path: 'miss-trichy',
    count: 15,
  },
  {
    title: 'Mini India Cultural Festival (2016)',
    description:
      'A grand celebration alongside Rotary District 3000 and National College Trichy, showcasing the rich tapestry of Indian culture and heritage.',
    path: 'mini-india',
    count: 15,
  },
  {
    title: 'Pattimandram',
    description:
      'Led by the charismatic Naduvar Madurai Muthu, this event united 100 Rotary Clubs from 8 districts in a joyous celebration of diversity and inclusivity.',
    path: 'pattimandram',
    count: 15,
  },
  {
    title: 'Dance and Voice of RID 3000 (2018)',
    description:
      'A captivating showcase of artistic talent within the Rotary District, providing a mesmerizing glimpse into the cultural wealth of the region.',
    path: 'dance-and-voice',
    count: 15,
  },
  {
    title: 'DJ Nte with Butterflies (2017)',
    description:
      'A harmonious fusion of beats and melodies, symbolizing unity through music, achieved through a seamless collaboration with the Rotary Club of Tiruchirapalli Butterflies and Innerwheel Club of Tiruchirapalli.',
    path: 'dj-nte',
    count: 15,
  },
  {
    title: 'Event Management',
    description:
      'Samuthrika Academy has etched an enduring legacy on the cultural canvas, orchestrating a plethora of events and cultivating partnerships that resonate with vitality and ingenuity.',
    path: 'events',
    count: 15,
  },
  {
    title: 'Singapennae',
    description:
      'Singapennae, hosted by Samuthrika Academy and Rotary Club of Tiruchi Thamizhachi, welcomed women from diverse clubs. The highlight was honouring 16 Rotary Ladies from Brazil as chief guests with the Singapennae sachet and crown.',
    path: 'singapennae',
    count: 15,
  },
  {
    title: 'Royal Queens',
    description:
      'Royal Queens, a Rotary event led by Rtn Thamizhachi Priya Govindaraj, celebrated women Rotarians with the sachet "Royal Queens" and crowns. The chief guest, a Miss India title holder and V Care proprietor, added glamour to the event.',
    path: 'royal-queens',
    count: 15,
  },
  {
    title: 'With Celebrities',
    description:
      'The collection of photos featuring Rtn Thamizhachi Priya Govindaraj with celebrities radiates happiness and joy, capturing moments of their meetings at various places and events.',
    path: 'with-celebrities',
    count: 15,
  },
  {
    title: 'As a Chief Guest',
    description:
      'Rtn Thamizhachi Priya Govindaraj actively participated in numerous events and programmes across Rotary Clubs, schools, and colleges. Below are a few clippings showcasing her engagement within these communities.',
    path: 'chief-guest',
    count: 15,
  },
];

const slidesFor = (event: Event) =>
  Array.from(
    { length: event.count },
    (_, index) =>
      `${BASE}/${event.path}/img_${String(index + 1).padStart(2, '0')}.jpg`
  );

const EventManagement = () => {
  return (
    <>
      <Banner
        image={`${BASE}/img_01.jpg`}
        alt="An event staged by Samuthrika Academy"
        tagline="A symphony of collaboration"
        title="Event Management"
        description="Samuthrika Academy has etched an enduring legacy on the cultural canvas, orchestrating a plethora of events and cultivating partnerships that resonate with vitality and ingenuity."
      >
        <ShareDialog
          url={window.location.href}
          title="Event Management - Samuthrika Academy"
          content="Samuthrika Academy has etched an enduring legacy on the cultural canvas, orchestrating a plethora of events and cultivating partnerships that resonate with vitality and ingenuity."
        />
      </Banner>

      {/*
        Each event is a stop on the spine with its own gallery beneath it.

        Previously every event was a two-column row with a carousel of unsized
        images crammed into one half — ten carousels down the page with no
        ordering, and each slide a bare `h-48 lg:h-80` with no width control, so
        a portrait photograph and a panorama sat in the same row at wildly
        different widths. ImageGallery crops them to a common aspect.
      */}
      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          <Spine>
            {events.map((event, index) => (
              <SpineItem
                key={event.path}
                marker={String(index + 1).padStart(2, '0')}
              >
                <div className="flex flex-col gap-6">
                  <Reveal className="flex max-w-2xl flex-col gap-2">
                    {/* h2, not Display3's default h3: the Banner above
                        carries this page's h1 and nothing else sits between
                        it and these section headings. */}
                    <Typography.Display3
                      as="h2"
                      className="text-2xl md:text-3xl lg:text-4xl"
                    >
                      {event.title}
                    </Typography.Display3>
                    <Typography.Body className="text-muted-foreground">
                      {event.description}
                    </Typography.Body>
                  </Reveal>

                  <ImageGallery
                    images={slidesFor(event)}
                    alt={`Photographs from ${event.title}`}
                  />
                </div>
              </SpineItem>
            ))}
          </Spine>
        </SectionLayout>
      </Band>
    </>
  );
};

export default EventManagement;
